import React, { memo } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core';
import {
  OfflineContentItem,
  OfflineContentItemNoTOC,
} from '../OfflineContentItem';
import { doesMapMatch } from '../OfflineHelpers';
import { ChapterList, LoadingButton } from 'components';
import { OFFLINE_CHAPTER_STATUS } from 'constants';
import { Typography } from '@redshelf/redshelf-ui';

const useStyles = makeStyles((theme) => {
  return {
    root: {
      ...theme.typography.body2,
    },
    sectionHeaderWrapper: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontWeight: theme.typography.fontWeightMedium,
      borderBottom: `1px solid ${theme.palette.grey[300]}`,
    },
    sectionHeader: {
      color: theme.palette.greyBlue[700],
      padding: theme.spacing(theme.typography.pxToRem(11.5), 2),
      fontWeight: theme.typography.fontWeightMedium,
    },
  };
});

const arePropsEqual = (prevProps, nextProps) => {
  return (
    doesMapMatch(prevProps.markerStatusMap, nextProps.markerStatusMap) &&
    prevProps.showWholeBookDownload === nextProps.showWholeBookDownload &&
    prevProps.isLoading === nextProps.isLoading
  );
};

const DownloadedContent = ({
  className,
  downloadedContent,
  downloadedContentRef,
  downloadedItemsRef,
  isLoading,
  markersShouldOpen,
  markerStatusMap,
  onClose,
  setIsDeleteAllDownloadsConfirmOpen,
  showWholeBookDownload,
  statusButtonOnClick,
  ...props
}) => {
  const classes = useStyles();

  const showChaptersList = downloadedContent.size > 0;
  const entireNoTOCbook = !downloadedContent.size && showWholeBookDownload;
  const showDownloadedContent = showChaptersList || entireNoTOCbook;

  return showDownloadedContent ? (
    <div
      ref={downloadedContentRef}
      className={clsx(classes.root, className)}
      {...props}
    >
      <div className={classes.sectionHeaderWrapper}>
        <Typography
          component="h3"
          variant="body2"
          className={classes.sectionHeader}
        >
          Downloaded Content
        </Typography>

        <LoadingButton
          isLoading={isLoading}
          onClick={() => setIsDeleteAllDownloadsConfirmOpen(true)}
        >
          Clear Downloads
        </LoadingButton>
      </div>

      {showChaptersList && (
        <ChapterList ref={downloadedItemsRef}>
          {[...downloadedContent].map((marker) => (
            <React.Fragment
              key={`${marker.label}-${marker.index}-${marker.id}`}
            >
              <OfflineContentItem
                marker={marker}
                markersShouldOpen={markersShouldOpen}
                markerStatusMap={markerStatusMap}
                onClose={onClose}
                statusButtonOnClick={statusButtonOnClick}
              />
            </React.Fragment>
          ))}
        </ChapterList>
      )}

      {entireNoTOCbook && (
        <OfflineContentItemNoTOC
          status={
            isLoading
              ? OFFLINE_CHAPTER_STATUS.DOWNLOADING
              : OFFLINE_CHAPTER_STATUS.DOWNLOADED
          }
          statusButtonOnClick={() => setIsDeleteAllDownloadsConfirmOpen(true)}
        />
      )}
    </div>
  ) : null;
};

DownloadedContent.propTypes = {
  // ref for the downloaded content
  downloadedContentRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  // ref for the downloaded items
  downloadedItemsRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  /**
   * @ignore
   */
  className: PropTypes.string,
  /**
   * Set of markers that have been downloaded.
   * Source of data used to display menu contents
   */
  downloadedContent: PropTypes.instanceOf(Set),
  /**
   * Passed to LoadingButton component, should button display a loading graphic
   */
  isLoading: PropTypes.bool,
  /**
   * Set of markers that should have open accordion by default
   */
  markersShouldOpen: PropTypes.instanceOf(Set),
  /**
   * Map of markers vs their offline status
   */
  markerStatusMap: PropTypes.instanceOf(Map),
  /**
   * Function to call when navigation to location closes menu
   */
  onClose: PropTypes.func,
  /**
   * passed to LoadingButton component
   */
  setIsDeleteAllDownloadsConfirmOpen: PropTypes.func,
  /**
   * passed to OfflineContentItem, onClick action for ContentStatusButton
   */
  statusButtonOnClick: PropTypes.func,
  /**
   * Boolean to determine if the title should show or not (for noTOC books)
   */
  showWholeBookDownload: PropTypes.bool,
};

export default memo(DownloadedContent, arePropsEqual);
